import {
  IBlockchainEntity,
  EBlockchainFeature,
  ChainsConfig,
  ALLORA_IDS,
  APTOS_IDS,
  ENABLED_KAVA_IDS,
  ENABLED_SECRET_NETWORK_IDS,
  ENABLED_ZETACHAIN_IDS,
  SEI_IDS,
  STELLAR_IDS,
  blockchainNameTemplate,
  userNameTemplate,
  ZERO_G_IDS,
  ChainID,
  EBlockchainType,
  TAC_IDS,
  MOVEMENT_IDS,
} from '@ankr.com/chains-list';

import { buildGrpcUrls } from './buildGrpcUrls';

const shouldUsePremiumHttpUrl = (id: string) => {
  const isTron = id === ChainID.TRON_REST;
  const isAptos = id === 'aptos';
  const isAptosTestnet = id === 'aptos_testnet';
  const isGnosisBeacon = id === 'gnosis_beacon';
  const isEthBeacon = id === 'eth_beacon';
  const isEthSepoliaBeacon = id === 'eth_sepolia_beacon';
  const isZetaChain = ENABLED_ZETACHAIN_IDS.includes(id);
  const isEnabledSecret = ENABLED_SECRET_NETWORK_IDS.includes(id);
  const isSei = SEI_IDS.includes(id);
  const isKava = ENABLED_KAVA_IDS.includes(id);
  const isStellar = STELLAR_IDS.includes(id);
  const isMovement = MOVEMENT_IDS.includes(id);
  const isAlloraTestnet = ALLORA_IDS.includes(id);
  const is0g = ZERO_G_IDS.includes(id as ChainID);
  const isBlockbook = id === 'btc_blockbook';
  const isBlockbookSignet = id === 'btc_blockbook_signet';
  const isTonRest = id === 'ton-rest';
  const isFuelRest = id === 'fuel-rest';
  const isFuelSepolia = id === 'fuel-sepolia';
  const isTac = TAC_IDS.includes(id as ChainID);
  const isSuiGraphql = id === ChainID.SUI_GRAPHQL;

  return (
    isTron ||
    isAptos ||
    isAptosTestnet ||
    isEnabledSecret ||
    isGnosisBeacon ||
    isEthBeacon ||
    isEthSepoliaBeacon ||
    isZetaChain ||
    isSei ||
    isKava ||
    isStellar ||
    isAlloraTestnet ||
    isBlockbook ||
    isBlockbookSignet ||
    is0g ||
    isTac ||
    isTonRest ||
    isFuelRest ||
    isFuelSepolia ||
    isSuiGraphql ||
    isMovement
  );
};

const isBeacon = (id: string) => id.includes('beacon');

const getPaths = (blockchain: IBlockchainEntity) => {
  let paths = [...(blockchain?.paths ?? [])];

  if (shouldUsePremiumHttpUrl(blockchain.id)) {
    paths = blockchain?.paths ? [blockchain.paths[1]] : [];

    if (isBeacon(blockchain.id) && !blockchain.paths?.[1]) {
      paths = blockchain.paths?.[0] ? [blockchain.paths[0]] : [];
    }
  }

  return paths;
};

export const buildRpcUrl = (
  privateRpcUrl: string,
  path: string,
  userEndpointToken: string,
) => {
  return privateRpcUrl
    .replace(blockchainNameTemplate, path)
    .replace(userNameTemplate, userEndpointToken);
};

const buildAptosUrl = (
  privateRpcUrl: string,
  path: string,
  userEndpointToken: string,
) => {
  const url = buildRpcUrl(privateRpcUrl, path, userEndpointToken);
  const lastPart = `${url.endsWith('/') ? '' : '/'}v1`;

  return `${url}${lastPart}`;
};

interface GetUrlsParams {
  paths: string[];
  privateUrl: string;
  userEndpointToken: string;
  isAptos: boolean;
}

const getUrls = ({
  paths,
  privateUrl,
  userEndpointToken,
  isAptos,
}: GetUrlsParams) => {
  return paths.map(path =>
    isAptos
      ? buildAptosUrl(privateUrl, path, userEndpointToken)
      : buildRpcUrl(privateUrl, path, userEndpointToken),
  );
};

interface BuildPrivateUrlsParams {
  blockchains: IBlockchainEntity[];
  enterpriseRpcUrl: string;
  enterpriseWsUrl: string;
  grpcURL: string;
  privateRpcUrl: string;
  privateWsUrl: string;
  userEndpointToken?: string;
}

export const buildPrivateUrls = ({
  blockchains,
  enterpriseRpcUrl,
  enterpriseWsUrl,
  grpcURL,
  privateRpcUrl,
  privateWsUrl,
  userEndpointToken = '',
}: BuildPrivateUrlsParams) => {
  return [...blockchains].reduce<ChainsConfig>((result, blockchain) => {
    const { id } = blockchain;
    const blockchainCopy = { ...blockchain }; // Create a shallow copy of the blockchain object

    const hasRPC =
      blockchain.features.includes(EBlockchainFeature.RPC) ||
      blockchain.features.includes(EBlockchainFeature.ComingSoon) ||
      blockchain.features.includes(EBlockchainFeature.GRPC_WEB);
    const hasWS = blockchain.features.includes(EBlockchainFeature.WS);

    const paths = getPaths(blockchain);

    const isAptos = APTOS_IDS.includes(id);

    if (id === ChainID.BTC_BLOCKBOOK_SIGNET) {
      blockchainCopy.type = EBlockchainType.Extension;
    }

    const rpcURLs = hasRPC
      ? getUrls({
          paths,
          privateUrl: privateRpcUrl,
          userEndpointToken,
          isAptos,
        })
      : [];

    const wsURLs = hasWS
      ? getUrls({
          paths,
          privateUrl: privateWsUrl,
          userEndpointToken,
          isAptos,
        })
      : [];

    const hasREST = blockchain.features.includes(EBlockchainFeature.REST);

    const restURLs = hasREST
      ? getUrls({
          paths,
          privateUrl: privateRpcUrl,
          userEndpointToken,
          isAptos,
        })
      : [];

    const hasGRPCURLs = blockchain.features.includes(EBlockchainFeature.GRPC);

    const enterpriseURLs = hasGRPCURLs
      ? []
      : getUrls({
          paths,
          privateUrl: enterpriseRpcUrl,
          userEndpointToken,
          isAptos,
        });

    const enterpriseWsURLs = hasGRPCURLs
      ? []
      : getUrls({
          paths,
          privateUrl: enterpriseWsUrl,
          userEndpointToken,
          isAptos,
        });

    const grpcURLs = hasGRPCURLs
      ? buildGrpcUrls({
          paths,
          grpcUrlTemplate: grpcURL,
          blockchainID: blockchain.id,
        })
      : [];

    result[id] = {
      blockchain: blockchainCopy,
      enterpriseURLs,
      enterpriseWsURLs,
      grpcURLs,
      restURLs,
      rpcURLs,
      wsURLs,
    };

    return result;
  }, {});
};
